import { TextField, InputAdornment, Box, alpha, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Iconify from "../../../minimals/components/iconify";
import { ContactSearchResult } from "./types/businessInsights";
import { SearchResults } from "./SearchResults";

export type SearchType = "Contacts" | "Companies";

type SearchBarProps = {
  searchTerm: string;
  searchType: SearchType;
  searchResults: ContactSearchResult[];
  isSearching: boolean;
  showDropdown: boolean;
  onSearchChange: (value: string) => void;
  onSearchTypeChange: (type: SearchType) => void;
  onFocus: () => void;
  onClickAway: () => void;
  onSelectContact: (contact: ContactSearchResult) => void;
  onCompanyClick?: (companyId: number, companyName: string) => void;
};

export function SearchBar({
  searchTerm,
  searchType,
  searchResults,
  isSearching,
  showDropdown,
  onSearchChange,
  onSearchTypeChange,
  onFocus,
  onClickAway,
  onSelectContact,
  onCompanyClick
}: SearchBarProps) {
  const theme = useTheme();

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Box position="relative" width="100%" maxWidth={600}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "stretch",
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            borderRadius: 2.5,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
            overflow: "hidden",
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.12)}`
            },
            "&:focus-within": {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}
        >
          <ToggleButtonGroup
            value={searchType}
            exclusive
            onChange={(_, value) => value && onSearchTypeChange(value)}
            size="small"
            sx={{
              borderRadius: 0,
              width: { xs: "100%", sm: "auto" },
              borderRight: { xs: "none", sm: `1px solid ${theme.palette.divider}` },
              borderBottom: { xs: `1px solid ${theme.palette.divider}`, sm: "none" },
              "& .MuiToggleButtonGroup-grouped": {
                border: "none",
                borderRadius: "0 !important",
                margin: 0,
                flex: { xs: 1, sm: "unset" }
              },
              "& .MuiToggleButton-root": {
                px: 2,
                py: 0,
                height: 40,
                fontWeight: 600,
                fontSize: "0.8rem",
                color: theme.palette.text.secondary,
                "&.Mui-selected": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15)
                  }
                },
                "&:hover": {
                  backgroundColor: alpha(theme.palette.action.hover, 0.5)
                }
              }
            }}
          >
            <ToggleButton value="Contacts">
              <Iconify icon="eva:person-outline" width={16} sx={{ mr: 0.5 }} />
              Contacts
            </ToggleButton>
            <ToggleButton value="Companies">
              <Iconify icon="mdi:domain" width={16} sx={{ mr: 0.5 }} />
              Companies
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField
            size="small"
            value={searchTerm}
            onFocus={onFocus}
            onChange={(event) => onSearchChange(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" width={20} sx={{ color: theme.palette.text.disabled }} />
                </InputAdornment>
              )
            }}
            inputProps={{
              autoComplete: "off",
              "data-lpignore": "true"
            }}
            placeholder={searchType === "Contacts" ? "Search by name..." : "Search by company..."}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                backgroundColor: "transparent",
                fontSize: "0.9rem",
                height: 40,
                "& fieldset": {
                  border: "none"
                },
                "&:hover fieldset": {
                  border: "none"
                },
                "&.Mui-focused fieldset": {
                  border: "none"
                }
              },
              "& .MuiOutlinedInput-input": {
                py: 0
              }
            }}
          />
        </Box>

        {showDropdown && searchTerm && (
          <SearchResults
            results={searchResults}
            isSearching={isSearching}
            onSelect={onSelectContact}
            onCompanyClick={onCompanyClick}
          />
        )}
      </Box>
    </ClickAwayListener>
  );
}
